import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type { DiffEntry } from '@common/types'
import { formatDateTime } from '@frontend/lib/utils'

/** Minimal shape shared by ReviewEdit and AuditorEdit for timeline rendering. */
export interface HistoryEntry {
  id: string
  editorEmail: string | null
  editedAt: string
  diff?: DiffEntry[] | null
}

function formatDiffValue(value: unknown): string {
  if (value === null || value === undefined) return '(empty)'
  if (typeof value === 'string') return value || '(empty)'
  if (Array.isArray(value)) {
    if (value.length === 0) return '(empty array)'
    return JSON.stringify(value, null, 2)
  }
  if (typeof value === 'object') return JSON.stringify(value, null, 2)
  return String(value)
}

export function EditHistoryTimeline({
  edits,
  emptyMessage = 'No changes have been recorded yet.',
}: {
  edits: HistoryEntry[]
  emptyMessage?: string
}) {
  const [expandedEdits, setExpandedEdits] = useState<Set<string>>(new Set())

  if (edits.length === 0) {
    return <div className="text-center py-8 text-muted-foreground">{emptyMessage}</div>
  }

  const toggleExpanded = (editId: string) => {
    setExpandedEdits((prev) => {
      const next = new Set(prev)
      if (next.has(editId)) {
        next.delete(editId)
      } else {
        next.add(editId)
      }
      return next
    })
  }

  return (
    <div className="space-y-4">
      {edits.map((edit) => {
        const isExpanded = expandedEdits.has(edit.id)
        const diff = edit.diff as DiffEntry[] | null

        return (
          <div key={edit.id} className="relative pl-6 border-l-2 border-border">
            <div className="absolute -left-1.5 top-1 h-3 w-3 rounded-full bg-primary" />
            <div className="pb-4">
              <div
                className="flex items-start gap-2 cursor-pointer hover:bg-muted/50 -ml-2 pl-2 py-1 rounded"
                onClick={() => toggleExpanded(edit.id)}
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 mt-0.5 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 mt-0.5 text-muted-foreground" />
                )}
                <div className="flex-1">
                  <div className="text-sm font-medium">{edit.editorEmail}</div>
                  <div className="text-xs text-muted-foreground">{formatDateTime(edit.editedAt)}</div>
                </div>
              </div>

              {isExpanded && diff && diff.length > 0 && (
                <div className="mt-3 space-y-2">
                  {diff.map((change, i) => (
                    <div key={i} className="bg-muted/50 rounded-md p-3 text-sm">
                      <div className="font-medium text-xs text-muted-foreground uppercase tracking-wide mb-2">
                        {change.field.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2">
                        <div className="bg-red-50 border border-red-200 rounded p-2">
                          <div className="text-xs text-red-600 font-medium mb-1">Previous</div>
                          <div className="text-xs break-words">{formatDiffValue(change.old)}</div>
                        </div>
                        <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                          <div className="text-xs text-emerald-600 font-medium mb-1">New</div>
                          <div className="text-xs break-words">{formatDiffValue(change.new)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {isExpanded && (!diff || diff.length === 0) && (
                <div className="mt-3 text-sm text-muted-foreground italic">
                  No detailed diff available for this edit.
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
